import cuid from "cuid";
import { get, isNil } from "lodash";

export const SCREEN_VERSION_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
};

export const DEFAULT_SOURCE_LOCALE = "fr-FR";

export const normalizeSlots = (slots) => (Array.isArray(slots) ? slots : []);

/** Strips the data URL prefix: the API expects raw base64. */
export const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("READ_FAILED"));
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.readAsDataURL(file);
  });

export const resolveSlotsForDraft = (versionSlots, states = []) => {
  const normalized = normalizeSlots(versionSlots);
  if (normalized.length) return normalized;
  for (const state of states) {
    const stateSlots = normalizeSlots(
      get(state, "bindingManifest.slots") || state.slots,
    );
    if (stateSlots.length) return stateSlots;
  }
  return [];
};

export const buildStatesPayload = (states = []) =>
  states.map((state) => ({
    id: cuid(),
    name: state.name,
    order: isNil(state.order) ? 0 : state.order,
    htmlContent: state.htmlContent || "",
    conditions: state.conditions || [],
    bindingManifest: {
      id: cuid(),
      slots: normalizeSlots(get(state, "bindingManifest.slots") || state.slots),
    },
  }));

/**
 * Creates a screen (when needed), a draft version, and optionally runs slot
 * detection when the draft has no slots to copy.
 */
export const createScreenDraft = async ({
  addScreen,
  addScreenVersion,
  detectSlots,
  statusId,
  screenId,
  description,
  versionNumber,
  htmlContent,
  styleContent,
  slots = [],
  states = [],
  copyStates = false,
  translations = {},
  published = false,
  updateScreen,
}) => {
  let nextScreenId = screenId;
  if (!nextScreenId) {
    nextScreenId = cuid();
    await addScreen({
      variables: {
        data: {
          id: nextScreenId,
          status: { id: statusId },
          description,
        },
      },
    });
  }

  const nextVersionNumber = versionNumber ? Number(versionNumber) + 1 : 1;
  const versionId = cuid();
  const draftSlots = resolveSlotsForDraft(slots, states);

  await addScreenVersion({
    variables: {
      data: {
        id: versionId,
        screen: { id: nextScreenId },
        versionNumber: nextVersionNumber,
        status: published
          ? SCREEN_VERSION_STATUS.PUBLISHED
          : SCREEN_VERSION_STATUS.DRAFT,
        htmlContent,
        styleContent,
        sourceLocale: DEFAULT_SOURCE_LOCALE,
        bindingManifest: {
          id: cuid(),
          slots: draftSlots,
        },
        translationTable: {
          id: cuid(),
          entries: translations || {},
        },
        ...(copyStates ? { states: buildStatesPayload(states) } : {}),
      },
    },
  });

  if (!draftSlots.length && detectSlots) {
    await detectSlots({
      variables: { screenVersion: { id: versionId } },
    });
  }

  if (published && updateScreen) {
    try {
      await updateScreen({
        variables: {
          where: { id: nextScreenId },
          data: { currentPublishedVersion: { id: versionId } },
        },
      });
    } catch (_error) {
      // Version is already stored as published; the pointer is best-effort.
    }
  }

  return { screenId: nextScreenId, versionId };
};
